import React, {
  useCallback, useDeferredValue, useEffect, useId, useMemo, useRef, useState,
} from 'react';
import {
  MdSearch, MdClose, MdPerson, MdMeetingRoom, MdCastForEducation, MdDirectionsBus,
  MdFamilyRestroom, MdHistory, MdKeyboardReturn, MdChevronLeft, MdRefresh,
} from 'react-icons/md';
import useSearchIndex from './useSearchIndex';
import { buildIndex, search, parseQuery, highlight, TYPES } from './searchEngine';
import './globalSearch.css';

const TYPE_ICON = {
  student: MdPerson,
  section: MdMeetingRoom,
  teacher: MdCastForEducation,
  driver: MdDirectionsBus,
  family: MdFamilyRestroom,
};

const INACTIVE_LABEL = { student: 'مغادر', teacher: 'متوقف', driver: 'متوقف' };

const RECENT_KEY = 'mtz.search.recent';
const RECENT_MAX = 6;
// Session storage, not local: recents are people's names, and nothing
// personal outlives the browser session (see ui/cache/index.js).
const readRecent = () => {
  try { return JSON.parse(sessionStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
};
const writeRecent = (keys) => {
  try { sessionStorage.setItem(RECENT_KEY, JSON.stringify(keys)); } catch { /* ignore */ }
};

const initials = (name) => {
  const w = String(name || '').replace(/^عائلة\s+/, '').trim().split(/\s+/);
  return w.length >= 2 ? w[0][0] + w[1][0] : (w[0] || '؟')[0];
};

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

function Marked({ parts }) {
  return parts.map((p, i) => (p.hit ? <mark key={i}>{p.text}</mark> : <React.Fragment key={i}>{p.text}</React.Fragment>));
}

/**
 * The navbar search. Type anything — a pupil's name, a parent's phone,
 * «ثامن», a bus number, «الأقساط» — and it answers from an in-memory
 * index as you type, grouped by kind, with the reason each result
 * matched and exactly where Enter will take you.
 *
 * `onNavigate(pageId, intent)` hands the choice to App, which opens the
 * page and lets it act on the intent (see app/navIntent.js).
 */
export default function GlobalSearch({ nav, onNavigate }) {
  const { data, loading, refreshing, error, ensureFresh, retry } = useSearchIndex();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState('all');
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState(readRecent);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const rootRef = useRef(null);
  const listId = useId();

  // Typing stays at full speed even on a slow machine: React renders the
  // input first and the result list when it has a spare moment.
  const deferred = useDeferredValue(query);
  const stale = deferred !== query;

  const records = useMemo(() => buildIndex(data, nav), [data, nav]);
  const byKey = useMemo(() => new Map(records.map((r) => [r.key, r])), [records]);
  const pageLabel = useMemo(() => Object.fromEntries(nav.map((p) => [p.id, p.label])), [nav]);

  const q = useMemo(() => parseQuery(deferred), [deferred]);

  const { groups, took } = useMemo(() => {
    const t0 = performance.now();
    const g = search(records, q);
    return { groups: g, took: performance.now() - t0 };
  }, [records, q]);

  // A scope shows one kind only, and all of it (up to 40), so «عرض الكل»
  // on a group of 23 pupils really shows the 23.
  const visibleGroups = useMemo(() => {
    if (scope === 'all') return groups;
    return search(records.filter((r) => r.type === scope), q, { limit: 40 });
  }, [groups, scope, records, q]);

  const recentItems = useMemo(
    () => recent.map((k) => byKey.get(k)).filter(Boolean).map((rec) => ({ rec, why: null })),
    [recent, byKey]
  );

  const showingRecent = !q.tokens.length;
  const flat = useMemo(() => {
    if (showingRecent) return recentItems;
    return visibleGroups.flatMap((g) => g.items);
  }, [showingRecent, recentItems, visibleGroups]);

  useEffect(() => { setActive(0); }, [deferred, scope]);

  // Keep the highlighted row in view while arrowing through the list.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const openSearch = useCallback(() => {
    setOpen(true);
    ensureFresh();
  }, [ensureFresh]);

  const close = useCallback(() => {
    setOpen(false);
    setScope('all');
    inputRef.current?.blur();
  }, []);

  // Ctrl/⌘+K anywhere, or «/» when not already typing in a field.
  useEffect(() => {
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) || e.target.isContentEditable;
      if (((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K' || e.code === 'KeyK'))
        || (e.key === '/' && !typing)) {
        e.preventDefault();
        openSearch();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openSearch]);

  // Clicking anywhere outside closes it, like any dropdown.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!rootRef.current?.contains(e.target)) close(); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  const choose = (rec, action = rec.actions[0]) => {
    if (!action) return;
    const next = [rec.key, ...recent.filter((k) => k !== rec.key)].slice(0, RECENT_MAX);
    setRecent(next);
    writeRecent(next);
    setQuery('');
    close();
    onNavigate(action.page, action.intent);
  };

  const scopes = useMemo(() => {
    const counts = Object.fromEntries(groups.map((g) => [g.type, g.total]));
    const present = Object.keys(TYPES).filter((t) => t !== 'page' && (records.some((r) => r.type === t)));
    return present.map((t) => ({ type: t, label: TYPES[t].group, count: counts[t] || 0 }));
  }, [groups, records]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) openSearch();
      setActive((i) => (flat.length ? (i + 1) % flat.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (flat.length ? (i - 1 + flat.length) % flat.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = flat[active];
      if (hit) choose(hit.rec);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (scope !== 'all') setScope('all');
      else if (query) setQuery('');
      else close();
    } else if (e.key === 'Tab' && open && q.tokens.length) {
      // Tab walks the kind filter: الكل → الطلاب → الشعب → …
      e.preventDefault();
      const order = ['all', ...scopes.filter((s) => s.count).map((s) => s.type)];
      const i = order.indexOf(scope);
      setScope(order[(i + (e.shiftKey ? order.length - 1 : 1)) % order.length]);
    }
  };

  const activeId = flat[active] ? `${listId}-${active}` : undefined;
  const indexed = data
    ? [
      data.students && `${data.students.length} طالب`,
      data.sections && `${data.sections.length} شعبة`,
      data.teachers && `${data.teachers.length} معلم`,
      data.drivers && `${data.drivers.length} سائق`,
      data.families && `${data.families.length} عائلة`,
    ].filter(Boolean).join(' · ')
    : '';

  let rowIndex = -1;
  const renderRow = (item) => {
    rowIndex += 1;
    const i = rowIndex;
    const { rec, why } = item;
    const isActive = i === active;
    const Icon = rec.icon || TYPE_ICON[rec.type];
    const primary = rec.actions[0];
    return (
      <div
        key={rec.key}
        id={`${listId}-${i}`}
        data-index={i}
        role="option"
        aria-selected={isActive}
        className={`gs-row gs-t-${rec.type}${isActive ? ' is-active' : ''}${rec.inactive ? ' is-inactive' : ''}`}
        onMouseMove={() => { if (!isActive) setActive(i); }}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => choose(rec)}
      >
        <span className="gs-avatar" aria-hidden="true">
          {rec.type === 'student' || rec.type === 'teacher' || rec.type === 'family'
            ? <span className="gs-initials">{initials(rec.title)}</span>
            : <Icon size={18} />}
        </span>
        <span className="gs-row-main">
          <span className="gs-row-title">
            <Marked parts={highlight(rec.title, q)} />
            {rec.inactive && <span className="gs-tag">{INACTIVE_LABEL[rec.type] || 'غير نشط'}</span>}
          </span>
          <span className="gs-row-sub">{rec.subtitle}</span>
          {why && (
            <span className="gs-row-why">
              <span className="gs-why-label">{why.label}:</span>
              <bdi className={why.phone ? 'gs-ltr' : undefined}>
                <Marked parts={highlight(why.value, q, { phone: why.phone && !!q.tokens[0]?.digits })} />
              </bdi>
            </span>
          )}
          {isActive && rec.actions.length > 1 && (
            <span className="gs-actions">
              {rec.actions.slice(1).map((a) => (
                <button
                  key={a.label}
                  type="button"
                  className="gs-action"
                  onClick={(e) => { e.stopPropagation(); choose(rec, a); }}
                >
                  {a.label}
                </button>
              ))}
            </span>
          )}
        </span>
        <span className="gs-dest">
          <span className="gs-dest-path">
            {pageLabel[primary.page]}
            <MdChevronLeft size={14} />
            {primary.label}
          </span>
          <span className="gs-enter"><MdKeyboardReturn size={14} /></span>
        </span>
      </div>
    );
  };

  let body;
  if (!data && (loading || !error)) {
    body = (
      <div className="gs-state">
        {[0, 1, 2, 3].map((k) => <div key={k} className="gs-skel" />)}
        <p>جارٍ تجهيز البحث…</p>
      </div>
    );
  } else if (!data && error) {
    body = (
      <div className="gs-state">
        <p>تعذّر تحميل فهرس البحث: {error}</p>
        <button type="button" className="gs-action" onClick={retry}><MdRefresh size={14} /> إعادة المحاولة</button>
      </div>
    );
  } else if (showingRecent) {
    body = recentItems.length ? (
      <div className="gs-group">
        <div className="gs-group-head"><MdHistory size={14} /> آخر ما فتحته</div>
        {recentItems.map(renderRow)}
      </div>
    ) : (
      <div className="gs-hints">
        <strong>ابحث في كل شيء من هنا</strong>
        <ul>
          <li>اسم طالب، أو جزء منه — <em>«يوسف حلبي»</em></li>
          <li>رقم هاتف ولي أمر — <em>«0536…»</em> أو آخر أرقامه</li>
          <li>شعبة أو صف — <em>«ثامن»</em></li>
          <li>سائق أو رقم باص، معلم، عائلة، أو اسم صفحة — <em>«الأقساط»</em></li>
          <li>ما تريد أن تعمله — <em>«كيف أضيف شعبة»</em></li>
        </ul>
      </div>
    );
  } else if (!visibleGroups.length) {
    body = (
      <div className="gs-state">
        <p className="gs-empty-title">لا نتائج لـ «{query.trim()}»</p>
        <p>تأكّد من الإملاء، جرّب جزءاً من الاسم، أو ابحث برقم الهاتف.</p>
      </div>
    );
  } else {
    body = visibleGroups.map((g) => (
      <div key={g.type} className="gs-group">
        <div className="gs-group-head">
          {g.label}
          <span className="gs-group-count">{g.total}</span>
          {scope === 'all' && g.total > g.items.length && (
            <button type="button" className="gs-more" onMouseDown={(e) => e.preventDefault()} onClick={() => setScope(g.type)}>
              عرض الكل
            </button>
          )}
        </div>
        {g.items.map(renderRow)}
      </div>
    ));
  }

  return (
    <div ref={rootRef} className={`gs${open ? ' is-open' : ''}`} data-guide="top-search">
      {open && <div className="gs-scrim" onMouseDown={close} />}
      <button
        type="button"
        className="gs-trigger"
        aria-label="بحث"
        onClick={() => { openSearch(); setTimeout(() => inputRef.current?.focus(), 0); }}
      >
        <MdSearch size={20} />
      </button>

      <div className="gs-box">
        <MdSearch className="gs-icon" size={19} />
        <input
          ref={inputRef}
          className="gs-input"
          type="search"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={open ? activeId : undefined}
          aria-autocomplete="list"
          autoComplete="off"
          spellCheck={false}
          placeholder="ابحث عن طالب، معلم، شعبة، سائق، رقم هاتف…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); if (!open) openSearch(); }}
          onFocus={openSearch}
          onKeyDown={onKeyDown}
        />
        {(stale || refreshing || (loading && open)) && <span className="gs-spin" aria-hidden="true" />}
        {query ? (
          <button
            type="button"
            className="gs-clear"
            aria-label="مسح"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setQuery(''); inputRef.current?.focus(); }}
          >
            <MdClose size={16} />
          </button>
        ) : (
          <kbd className="gs-kbd">{isMac ? '⌘' : 'Ctrl'} K</kbd>
        )}
        <button type="button" className="gs-cancel" onMouseDown={(e) => e.preventDefault()} onClick={close}>إلغاء</button>
      </div>

      {open && (
        <div className="gs-panel" role="presentation" data-guide-pop>
          {!showingRecent && data && (
            <div className="gs-scopes" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={scope === 'all'}
                className={`gs-scope${scope === 'all' ? ' is-active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setScope('all')}
              >
                الكل
              </button>
              {scopes.map((s) => (
                <button
                  key={s.type}
                  type="button"
                  role="tab"
                  aria-selected={scope === s.type}
                  disabled={!s.count}
                  className={`gs-scope${scope === s.type ? ' is-active' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setScope(s.type)}
                >
                  {s.label}
                  <span className="gs-scope-count">{s.count}</span>
                </button>
              ))}
            </div>
          )}

          <div ref={listRef} id={listId} role="listbox" className={`gs-list${stale ? ' is-stale' : ''}`}>
            {body}
          </div>

          <div className="gs-foot">
            <span className="gs-keys">
              <kbd>↑</kbd><kbd>↓</kbd> تنقّل
              <kbd>Enter</kbd> فتح
              {!showingRecent && <><kbd>Tab</kbd> النوع</>}
              <kbd>Esc</kbd> إغلاق
            </span>
            {data && (
              <span className="gs-stats">
                {!showingRecent && <><bdi dir="ltr">{took < 1 ? '< 1' : Math.round(took)} ms</bdi> · </>}{indexed}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

